import { yupResolver } from '@hookform/resolvers/yup';
import { Divider, Input, Space, Typography } from 'antd';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import AnimateHeight from 'react-animate-height';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { useMaybeFetchServiceFee } from '../../hooks/useMaybeFetchServiceFee';
import { massPropTypes } from '../../propTypes/mass';
import selectors from '../../store/selectors';
import FormInputError from '../Form/FormInputError';
import OfferingAmount from '../Form/OfferingAmount';
import SubmitAddMassOffering from '../Form/rhf/SubmitAddMassOffering/SubmitAddMassOffering';
import ServiceFee from '../ServiceFee/ServiceFee';

import ChoosePaymentType from './components/ChoosePaymentType/ChoosePaymentType';
import { FORM_FIELDS, MASS_OFFERINGS_PAYMENT_TYPES } from './constants';

const useAddMassOfferingForm = (
  isMassOfferingPhoneNumberRequired,
  allowFreeOfferings,
  massOfferingAmount,
  minimalOfferingsAmountFromMass,
) => {
  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);
  const isServiceFeeSelectedCoveredDefault = useSelector(
    selectors.selectIsServiceFeeCoveredDefault,
  );
  const isServiceFeeCoveredRequired = useSelector(selectors.selectIsServiceFeeCoveredRequired);

  const hasPredefinedValues = massOfferingAmount?.predefinedValues?.length > 1;
  let minOfferingAmount = 0;
  if (allowFreeOfferings) {
    minOfferingAmount = 0;
  } else if (minimalOfferingsAmountFromMass !== null) {
    minOfferingAmount = minimalOfferingsAmountFromMass;
  } else {
    minOfferingAmount = massOfferingAmount?.minimalValue ?? 1;
  }

  const addMassOfferingFormSchema = useRef(
    yup.object().shape({
      [FORM_FIELDS.DONATOR_NAME]: yup.string().required('Podaj swoje imię i nazwisko'),
      [FORM_FIELDS.DONATOR_EMAIL]: yup
        .string()
        .email('Podaj poprawny adres email')
        .required('Podaj adres email'),
      ...(isMassOfferingPhoneNumberRequired && {
        [FORM_FIELDS.DONATOR_PHONE]: yup
          .string()
          .matches(
            /[+ 0-9]{9,}/,
            'Podaj poprawny numer telefonu (bez spacji, kropek, myślników, itp.)',
          )
          .required('Podaj numer telefonu'),
      }),
      [FORM_FIELDS.OFFERING_CONTENT]: yup
        .string()
        .max(300, 'Treść intencji może mieć maksymalnie 300 znaków')
        .required('Podaj treść intencji'),
      [FORM_FIELDS.OFFERING_AMOUNT]: yup
        .number()
        .typeError('Podaj kwotę ofiary')
        .required('Podaj kwotę ofiary')
        .test(
          'is-amount-valid',
          ({ value }) => {
            if (value < 0) {
              return 'Kwota nie może być ujemna';
            }

            if (value < minOfferingAmount) {
              return `Minimalna kwota ofiary składanej za pomocą formularza online to ${minOfferingAmount} PLN. Aby złożyć niższą ofiarę skontaktuj się bezpośrednio z kancelarią parafialną.`;
            }

            return undefined;
          },
          value => {
            return value >= minOfferingAmount;
          },
        ),
      [FORM_FIELDS.FORM_AGREEMENT]: yup.boolean().oneOf([true], 'Zaakceptuj regulamin'),

      [FORM_FIELDS.PAYMENT_TYPE]: yup
        .string()
        .oneOf(Object.values(MASS_OFFERINGS_PAYMENT_TYPES), 'Wybierz sposób złożenia ofiary'),
    }),
  );

  const defaultOfferAmountValue = hasPredefinedValues
    ? massOfferingAmount.predefinedValues[1]
    : null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
    clearErrors,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(addMassOfferingFormSchema.current),
    mode: 'onBlur',
    defaultValues: {
      [FORM_FIELDS.OFFERING_AMOUNT]: defaultOfferAmountValue,
      [FORM_FIELDS.TOTAL_AMOUNT_TO_PAY]: defaultOfferAmountValue,
      [FORM_FIELDS.USER_COVERS_SERVICE_FEE]:
        isServiceFeeEnabled && (isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault),
      [FORM_FIELDS.PAYMENT_TYPE]: MASS_OFFERINGS_PAYMENT_TYPES.online,
    },
  });

  return {
    register,
    handleSubmit,
    errors,
    control,
    setError,
    clearErrors,
    watch,
    setValue,
  };
};

const AddMassOfferingForm = ({ selectedMass, onSubmit, loading }) => {
  const regulationsUrl = useSelector(selectors.selectRegulationsUrl);
  const massOfferingReservation = useSelector(selectors.selectMassOfferingReservation);
  const isMassOfferingReservationEnabled =
    massOfferingReservation.paymentToBeChosenReservation.enabled;

  const isMassOfferingPhoneNumberRequired = useSelector(
    selectors.isMassOfferingPhoneNumberRequired,
  );
  const massOfferingAmount = useSelector(selectors.selectMassOfferingAmountOption);
  const isServiceFeeEnabled = useSelector(selectors.selectIsServiceFeeEnabled);
  const isServiceFeeSelectedCoveredDefault = useSelector(
    selectors.selectIsServiceFeeCoveredDefault,
  );
  const isServiceFeeCoveredRequired = useSelector(selectors.selectIsServiceFeeCoveredRequired);

  const {
    allowFreeOfferings,
    massDate,
    minimalOfferingsAmount: minimalOfferingsAmountFromMass,
  } = selectedMass;

  const { register, handleSubmit, errors, control, setError, clearErrors, watch, setValue } =
    useAddMassOfferingForm(
      isMassOfferingPhoneNumberRequired,
      allowFreeOfferings,
      massOfferingAmount,
      minimalOfferingsAmountFromMass,
    );

  const totalAmountToPay = watch(FORM_FIELDS.TOTAL_AMOUNT_TO_PAY);
  const offeringAmount = watch(FORM_FIELDS.OFFERING_AMOUNT);
  const paymentType = watch(FORM_FIELDS.PAYMENT_TYPE);
  const isOnlinePayment = paymentType === MASS_OFFERINGS_PAYMENT_TYPES.online;

  const { loading: serviceFeeLoading } = useMaybeFetchServiceFee({
    isServiceFeeEnabled,
    isServiceFeeSelectedCoveredDefault:
      isServiceFeeCoveredRequired || isServiceFeeSelectedCoveredDefault,
    offeringAmount,
    setTotalAmountToPay: value => setValue(FORM_FIELDS.TOTAL_AMOUNT_TO_PAY, value),
    userCoversServiceFee: watch(FORM_FIELDS.USER_COVERS_SERVICE_FEE),
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Space direction="vertical" className="d-flex mt-4" size="large">
        <div>
          <Controller
            control={control}
            name={FORM_FIELDS.OFFERING_CONTENT}
            render={({ field }) => {
              return (
                <Input.TextArea
                  placeholder="Np. O zdrowie i błogosławieństwo dla Joanny w dniu imienin"
                  rows={3}
                  showCount
                  maxLength={300}
                  allowClear
                  disabled={loading}
                  name={field.name}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  value={field.value}
                />
              );
            }}
          />

          {errors[FORM_FIELDS.OFFERING_CONTENT] && (
            <FormInputError errorText={errors[FORM_FIELDS.OFFERING_CONTENT].message} />
          )}
        </div>

        <div>
          <Divider className="mt-0" />

          <Typography.Title level={4} className="mb-3">
            {isMassOfferingReservationEnabled ? 'Sposob rezerwacji terminu intencji' : 'Ofiara'}
          </Typography.Title>

          <Space direction="vertical" size="large" className="w-100">
            {isMassOfferingReservationEnabled && (
              <ChoosePaymentType
                control={control}
                inputFieldName={FORM_FIELDS.PAYMENT_TYPE}
                massDate={massDate}
                reservationTimeInDays={
                  massOfferingReservation.paymentToBeChosenReservation.reservationTimeInDays
                }
                minDaysBefore={
                  massOfferingReservation.paymentToBeChosenReservation.availableMinimumXDaysBefore
                }
              />
            )}

            <AnimateHeight duration={500} height={isOnlinePayment ? 'auto' : 0}>
              <Space direction="vertical" size="large">
                <Controller
                  control={control}
                  name={FORM_FIELDS.OFFERING_AMOUNT}
                  render={({ field }) => {
                    return (
                      <OfferingAmount
                        allowFreeOfferings={allowFreeOfferings}
                        clearErrors={clearErrors}
                        loading={loading}
                        massOfferingAmount={massOfferingAmount}
                        minimalMassOfferingAmount={
                          minimalOfferingsAmountFromMass ?? massOfferingAmount.minimalValue
                        }
                        name={field.name}
                        onBlur={field.onBlur}
                        onChange={field.onChange}
                        ref={field.ref}
                        setError={setError}
                        value={field.value}
                        showLabel
                      />
                    );
                  }}
                />
                {errors[FORM_FIELDS.OFFERING_AMOUNT] && (
                  <FormInputError errorText={errors[FORM_FIELDS.OFFERING_AMOUNT].message} />
                )}

                {isServiceFeeEnabled && (
                  <ServiceFee
                    inputFieldName={FORM_FIELDS.USER_COVERS_SERVICE_FEE}
                    isRequired={isServiceFeeCoveredRequired}
                    offeringAmount={offeringAmount}
                    register={register}
                    serviceFeeLoading={serviceFeeLoading}
                    totalAmountToPay={totalAmountToPay}
                  />
                )}
              </Space>
            </AnimateHeight>
          </Space>
        </div>

        <div>
          <Divider className="mt-0" />

          <Typography.Title level={4} className="mb-3">
            Dane Zamawiającego
          </Typography.Title>

          <Space direction="vertical" size="large" className="w-100">
            <div className="md-form input-group mt-0 mb-0">
              <input
                disabled={loading}
                className="form-control"
                autoComplete="name"
                type="text"
                id={FORM_FIELDS.DONATOR_NAME}
                {...register(FORM_FIELDS.DONATOR_NAME)}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor={FORM_FIELDS.DONATOR_NAME}>Imię i nazwisko</label>

              {errors[FORM_FIELDS.DONATOR_NAME] && (
                <FormInputError errorText={errors[FORM_FIELDS.DONATOR_NAME].message} />
              )}
            </div>

            <div className="md-form input-group mt-0 mb-0">
              <input
                disabled={loading}
                className="form-control"
                id={FORM_FIELDS.DONATOR_EMAIL}
                type="email"
                autoComplete="email"
                {...register(FORM_FIELDS.DONATOR_EMAIL)}
              />
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label htmlFor={FORM_FIELDS.DONATOR_EMAIL}>E-mail</label>

              {errors[FORM_FIELDS.DONATOR_EMAIL] && (
                <FormInputError errorText={errors[FORM_FIELDS.DONATOR_EMAIL].message} />
              )}
            </div>

            {isMassOfferingPhoneNumberRequired && (
              <div className="md-form input-group mt-0 mb-0">
                <input
                  disabled={loading}
                  className="form-control"
                  name={FORM_FIELDS.DONATOR_PHONE}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  id={FORM_FIELDS.DONATOR_PHONE}
                  {...register(FORM_FIELDS.DONATOR_PHONE)}
                />
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor={FORM_FIELDS.DONATOR_PHONE}>Numer telefonu</label>

                {errors[FORM_FIELDS.DONATOR_PHONE] && (
                  <FormInputError errorText={errors[FORM_FIELDS.DONATOR_PHONE].message} />
                )}
              </div>
            )}
          </Space>
        </div>

        <div className="md-form input-group mt-0 mb-0">
          <input
            disabled={loading}
            className="form-check-input"
            type="checkbox"
            value="1"
            id={FORM_FIELDS.FORM_AGREEMENT}
            {...register(FORM_FIELDS.FORM_AGREEMENT)}
          />
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="form-check-label w-100" htmlFor={FORM_FIELDS.FORM_AGREEMENT}>
            Akceptuję{' '}
            <a target="_blank" rel="noreferrer" href={regulationsUrl}>
              Regulamin
            </a>
          </label>

          {errors[FORM_FIELDS.FORM_AGREEMENT] && (
            <FormInputError
              className="mt-2"
              errorText={errors[FORM_FIELDS.FORM_AGREEMENT].message}
            />
          )}
        </div>
      </Space>

      <SubmitAddMassOffering
        name={FORM_FIELDS.OFFERING_AMOUNT}
        watch={watch}
        loading={loading}
        disabled={serviceFeeLoading}
        isOnlinePayment={isOnlinePayment}
        isPaymentExpiring
      />
    </form>
  );
};

AddMassOfferingForm.propTypes = {
  selectedMass: massPropTypes,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool,
};

export default AddMassOfferingForm;
